import { format } from 'date-fns';
import Api from 'services/api';

export const AMBASSADOR_INVITATION_PATH = '/embaixadores';

export const ambassadorToolPath = (slug: string): string =>
  `${AMBASSADOR_INVITATION_PATH}/ferramentas/${encodeURIComponent(slug)}`;

export const ambassadorFeedbackPath = (slug: string): string =>
  `${ambassadorToolPath(slug)}/feedback`;

export type AmbassadorAnswer = 'yes' | 'no' | 'partially';

export interface AmbassadorFeedback {
  tool_slug: string;
  solved_need: AmbassadorAnswer;
  easy_to_use: AmbassadorAnswer;
  /** Nota de 0 a 10 para a ferramenta. */
  rating: number;
  comment: string;
}

/** Passos registrados no servidor para o painel do programa no CRM. */
export type AmbassadorProgressStep = 'video_watched' | 'test_started';

export type AmbassadorProgress = Record<AmbassadorProgressStep, boolean>;

/**
 * Situação da ferramenta para o embaixador, calculada pelo backend
 * (Ambassador::Journey): bloqueada até o feedback da anterior, liberada,
 * em teste dentro do prazo ou com o prazo encerrado.
 */
export type AmbassadorToolStatus = 'locked' | 'available' | 'testing' | 'expired';

export interface AmbassadorToolState {
  slug: string;
  status: AmbassadorToolStatus;
  /** Último dia do teste (AAAA-MM-DD); null antes de iniciar. */
  test_until: string | null;
  /** Dia em que a ferramenta abre para os embaixadores (AAAA-MM-DD). */
  available_on: string | null;
  feedback_sent: boolean;
}

// Perfil guardado antes desta regra não traz as ferramentas: fica tudo
// bloqueado até o painel recarregar o perfil.
export const findAmbassadorToolState = (
  tools: AmbassadorToolState[] | undefined,
  slug: string,
): AmbassadorToolState =>
  tools?.find((tool) => tool.slug === slug) ?? {
    slug,
    status: 'locked',
    test_until: null,
    available_on: null,
    feedback_sent: false,
  };

// A data de liberação ainda não chegou (a comparação de AAAA-MM-DD como texto
// segue a ordem das datas).
export const isAwaitingRelease = (state: AmbassadorToolState): boolean =>
  Boolean(state.available_on && state.available_on > format(new Date(), 'yyyy-MM-dd'));

// Embaixador de cliente assinante do boletim, com a ferramenta dentro do prazo.
export const isAmbassadorTesting = (
  ambassador: { member?: boolean; subscriber?: boolean; tools?: AmbassadorToolState[] } | undefined,
  slug: string,
): boolean =>
  Boolean(ambassador?.member) &&
  ambassador?.subscriber === true &&
  findAmbassadorToolState(ambassador?.tools, slug).status === 'testing';

/** AAAA-MM-DD → DD/MM */
export const formatAmbassadorDate = (date: string): string => {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
};

// O link da comunidade vem da API; só é usado se for https.
const externalUrl = (value: unknown): string | null =>
  typeof value === 'string' && value.startsWith('https://') ? value : null;

// O Api devolve a resposta de erro (ou undefined, sem rede) em vez de rejeitar.
export const acceptAmbassadorInvitation = async (): Promise<{
  communityUrl: string | null;
} | null> => {
  const response = await Api.post(`${AMBASSADOR_INVITATION_PATH}/participacao`);
  if (response?.status !== 200 && response?.status !== 201) return null;

  return { communityUrl: externalUrl(response.data?.community_url) };
};

export const sendAmbassadorFeedback = async (feedback: AmbassadorFeedback): Promise<boolean> => {
  const response = await Api.post(`${AMBASSADOR_INVITATION_PATH}/feedbacks`, { feedback });
  return response?.status === 201;
};

// Andamento do usuário logado na ferramenta; null quando não deu para consultar.
export const fetchAmbassadorProgress = async (
  toolSlug: string,
): Promise<AmbassadorProgress | null> => {
  const response = await Api.get(`${AMBASSADOR_INVITATION_PATH}/progresso`, {
    params: { tool_slug: toolSlug },
  }).catch(() => undefined);
  return response?.status === 200 ? response.data : null;
};

const postProgress = (toolSlug: string, step: AmbassadorProgressStep) =>
  Api.post(`${AMBASSADOR_INVITATION_PATH}/progresso`, {
    progress: { tool_slug: toolSlug, step },
  }).catch(() => undefined);

// Registro para acompanhamento: uma falha aqui nunca pode travar o embaixador.
export const recordAmbassadorProgress = (toolSlug: string, step: AmbassadorProgressStep): void => {
  postProgress(toolSlug, step);
};

// O início do teste abre o prazo e libera a ferramenta no servidor: sem ele
// registrado, o embaixador não entra.
export const startAmbassadorTest = async (toolSlug: string): Promise<boolean> => {
  const response = await postProgress(toolSlug, 'test_started');
  return response?.status === 204;
};
